/**
 * CompetitorAnalysisModal
 * ========================
 * Displays the DataForSEO-powered Competitor Analysis:
 *   - Actual SERP market leaders with Gemini relevance scoring & reasoning
 *   - Perceived vs Actual competitor gap analysis
 *   - SERP visibility, keyword overlap count, avg position per domain
 *
 * Backwards compatible with the legacy API shape — new DataForSEO fields
 * (relevance_score, reasoning, is_direct_competitor, keywords_overlap_count,
 *  serp_visibility, llm_evaluated) are rendered when present.
 */

import React, { useEffect, useState } from 'react';
import './CompetitorAnalysisModal.css';
import { API_BASE_URL } from '../../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OverlappingKeyword {
    keyword: string;
    positions: number[];
    best_position: number;
}

export interface ActualCompetitor {
    domain: string;
    score: number;
    priority: string;
    similarity: number;
    frequency: number;
    coverage: number;
    keywords_matched: string[];
    avg_position?: number | null;
    // DataForSEO + Gemini enriched fields
    relevance_score?: number;
    serp_visibility?: number;
    keywords_overlap_count?: number;
    domain_intersections?: number;
    domain_avg_position?: number | null;
    organic_etv?: number;
    reasoning?: string;
    is_direct_competitor?: boolean;
    llm_evaluated?: boolean;
    overlapping_keywords?: OverlappingKeyword[];
}

export interface PerceivedCompetitor {
    domain: string;
    competitor_name: string;
    source: string;
    actual_rank: number | null;
    in_top_10: boolean;
    visibility_score: number;
    perception_gap: string;
    keywords_matched: string[];
}

export interface AnalysisData {
    actual_competitors: ActualCompetitor[];
    perceived_competitors: PerceivedCompetitor[];
    insights: {
        perception_vs_reality: string;
        total_perceived: number;
        correctly_identified: number;
        not_in_top_10: number;
    };
    keywords_analyzed: number;
    from_cache: boolean;
    is_locked?: boolean;
    sync_source?: string;
    cache_expires_at?: string;
    engine_status?: string;    // SUCCESS | PARTIAL_FALLBACK | FAILED
}

interface CompetitorAnalysisModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const getPriorityBadgeClass = (priority: string): string => {
    switch (priority) {
        case 'CRITICAL': return 'priority-critical';
        case 'HIGH':     return 'priority-high';
        case 'MEDIUM':   return 'priority-medium';
        default:         return 'priority-low';
    }
};

const getRelevanceBadge = (score: number, isDirect?: boolean) => {
    const pct = Math.round(score * 100);
    if (isDirect && pct >= 80) return { label: `${pct}%`, cls: 'relevance-high' };
    if (pct >= 50)              return { label: `${pct}%`, cls: 'relevance-medium' };
    return                               { label: `${pct}%`, cls: 'relevance-low' };
};

// ── Component ─────────────────────────────────────────────────────────────────

const CompetitorAnalysisModal: React.FC<CompetitorAnalysisModalProps> = ({
    isOpen,
    onClose,
    userId,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState<string | null>(null);
    const [data, setData]       = useState<AnalysisData | null>(null);
    const [expandedDomain, setExpandedDomain] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && userId) {
            fetchCompetitorAnalysis();
        }
    }, [isOpen, userId]);

    const fetchCompetitorAnalysis = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(
                `${API_BASE_URL}/asis/competitor-analysis`,
                {
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                }
            );

            if (!response.ok) {
                const errorPayload = await response
                    .json()
                    .catch(() => ({ detail: `HTTP ${response.status}` }));
                throw new Error(errorPayload.detail || 'Failed to fetch competitor analysis');
            }

            const result = await response.json();

            if (result.status === 'success') {
                setData(result.data);
            } else {
                setError(result.message || 'Failed to analyze competitors');
            }
        } catch (err: any) {
            setError(err.message || 'Error fetching competitor analysis');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content-competitor" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="modal-header">
                    <h2>🏆 Competitive Analysis: Perception vs Reality</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body">

                    {/* Loading */}
                    {loading && (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Analysing competitors…</p>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="error-state">
                            <p>❌ {error}</p>
                            <button onClick={fetchCompetitorAnalysis}>Retry</button>
                        </div>
                    )}

                    {/* Content */}
                    {!loading && !error && data && (
                        <>
                            {/* Status badges */}
                            <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                {/* AS-IS Lock state badge */}
                                {data.is_locked && (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        background: '#fef2f2', color: '#991b1b',
                                        border: '1px solid #fecaca',
                                        padding: '4px 10px', borderRadius: '999px',
                                        fontSize: '12px', fontWeight: 600,
                                    }}>
                                        🔒 AS-IS Baseline Locked (90-Day Cycle)
                                    </span>
                                )}

                                {/* Cache / live badge */}
                                {data.from_cache ? (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        background: '#dcfce7', color: '#166534',
                                        padding: '4px 10px', borderRadius: '999px',
                                        fontSize: '12px', fontWeight: 600,
                                    }}>
                                        📦 Cached
                                        {data.cache_expires_at && (
                                            <> · Valid until {new Date(data.cache_expires_at).toLocaleDateString('en-IN', {
                                                day: 'numeric', month: 'short', year: 'numeric',
                                            })}</>
                                        )}
                                    </span>
                                ) : (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        background: '#dbeafe', color: '#1e40af',
                                        padding: '4px 10px', borderRadius: '999px',
                                        fontSize: '12px', fontWeight: 600,
                                    }}>
                                        🔄 Live Analysis · Marketing4Sight AI
                                    </span>
                                )}

                                {/* Fallback warning */}
                                {data.engine_status === 'PARTIAL_FALLBACK' && (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        background: '#fef9c3', color: '#854d0e',
                                        padding: '4px 10px', borderRadius: '999px',
                                        fontSize: '12px', fontWeight: 600,
                                    }}>
                                        ⚠️ AI temporarily unavailable — sorted by SERP metrics
                                    </span>
                                )}
                            </div>

                            {/* Insight banner */}
                            <div className="insights-banner">
                                <div className="insight-main">{data.insights.perception_vs_reality}</div>
                                <div className="insight-stats">
                                    <span>Analysed {data.keywords_analyzed} keywords</span>
                                    <span>•</span>
                                    <span>
                                        {data.insights.correctly_identified} of{' '}
                                        {data.insights.total_perceived} perceived competitors confirmed
                                    </span>
                                </div>
                            </div>

                            {/* ── Actual Competitors (DataForSEO + Gemini) ── */}
                            <div className="section">
                                <h3>📊 Market Leaders (Top {data.actual_competitors.length})</h3>
                                <p className="section-desc">
                                    Discovered competitors based on your locked keywords
                                </p>

                                <div className="table-container">
                                    <table className="competitor-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Domain</th>
                                                <th>Relevance</th>
                                                <th>Priority</th>
                                                <th>Keyword Overlap</th>
                                                <th>SERP Visibility</th>
                                                <th>Avg Position</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.actual_competitors.map((comp, idx) => {
                                                const rel = comp.relevance_score ?? comp.score / 100;
                                                const { label: relLabel, cls: relCls } = getRelevanceBadge(rel, comp.is_direct_competitor);
                                                
                                                // Extract list of overlapping keywords from overlapping_keywords or keywords_matched
                                                const overlappingList: OverlappingKeyword[] = (comp.overlapping_keywords && comp.overlapping_keywords.length > 0)
                                                    ? comp.overlapping_keywords
                                                    : (comp.keywords_matched || []).map(kw => ({
                                                        keyword: kw,
                                                        positions: [],
                                                        best_position: 0,
                                                    }));

                                                const rawVis = comp.serp_visibility ?? comp.similarity ?? 0;
                                                // Strictly bounded 0.0% to 100.0%
                                                const boundedPct = Math.min(100, Math.max(0, rawVis <= 1.0 ? rawVis * 100 : rawVis));
                                                const formattedVis = `${boundedPct.toFixed(1)}%`;
                                                const isExpanded = expandedDomain === comp.domain;

                                                return (
                                                    <tr key={comp.domain} title={comp.reasoning || ''}>
                                                        <td className="rank-cell">#{idx + 1}</td>
                                                        <td className="domain-cell">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                {comp.is_direct_competitor && (
                                                                    <span style={{
                                                                        fontSize: '10px', fontWeight: 700,
                                                                        background: '#fce7f3', color: '#be185d',
                                                                        padding: '1px 5px', borderRadius: '4px',
                                                                    }}>DIRECT</span>
                                                                )}
                                                                <span>{comp.domain}</span>
                                                            </div>
                                                            {comp.reasoning && (
                                                                <div style={{
                                                                    fontSize: '11px', color: '#6b7280',
                                                                    marginTop: '4px', lineHeight: 1.4,
                                                                    maxWidth: '280px', whiteSpace: 'normal',
                                                                }}>
                                                                    {comp.reasoning}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`relevance-badge ${relCls}`}>
                                                                {relLabel}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`priority-badge ${getPriorityBadgeClass(comp.priority)}`}>
                                                                {comp.priority}
                                                            </span>
                                                        </td>
                                                        <td className="keyword-overlap-cell">
                                                            <div className="kw-overlap-wrapper">
                                                                {overlappingList.length > 0 ? (
                                                                    <>
                                                                        <span className="kw-overlap-count-badge">
                                                                            {overlappingList.length} {overlappingList.length === 1 ? 'Overlapping Keyword' : 'Overlapping Keywords'}
                                                                        </span>

                                                                        {/* Overlapping keywords with their ranks */}
                                                                        <div className="kw-rank-list">
                                                                            {(isExpanded
                                                                                ? overlappingList
                                                                                : overlappingList.slice(0, 2)
                                                                            ).map((ok) => (
                                                                                <div
                                                                                    key={ok.keyword}
                                                                                    className="kw-rank-item"
                                                                                    title={ok.best_position ? `"${ok.keyword}" ranks #${ok.best_position} on Google SERP` : ok.keyword}
                                                                                >
                                                                                    <span className="kw-rank-bullet">•</span>
                                                                                    <span className="kw-rank-name">{ok.keyword}</span>
                                                                                    {ok.best_position > 0 && (
                                                                                        <span className="kw-rank-pill">#{ok.best_position}</span>
                                                                                    )}
                                                                                </div>
                                                                            ))}

                                                                            {overlappingList.length > 2 && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="kw-dropdown-toggle"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setExpandedDomain(isExpanded ? null : comp.domain);
                                                                                    }}
                                                                                >
                                                                                    {isExpanded
                                                                                        ? '▲ Show less'
                                                                                        : `+${overlappingList.length - 2} more keywords ▼`}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <div className="kw-zero-overlap">
                                                                        <span className="kw-overlap-count-badge zero">0 Overlapping Keywords</span>
                                                                        <span className="kw-zero-note">No rankings in Top 100 SERPs</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="visibility-cell">
                                                            {formattedVis}
                                                        </td>
                                                        <td className="avg-pos-cell">
                                                            {comp.avg_position !== null && comp.avg_position !== undefined ? (
                                                                <span style={{ fontWeight: 600, color: '#1f2937', fontSize: '14px' }}>
                                                                    {Number(comp.avg_position).toFixed(1)}
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: '#9ca3af', fontWeight: 500 }}>—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* AI attribution */}
                                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                                    {data.actual_competitors.some(c => c.llm_evaluated)
                                        ? '✨ Relevance scored by AI against your business profile · Exact SERP keyword overlap shown with ranking positions'
                                        : '📐 Scored by SERP metrics (AI temporarily unavailable)'}
                                </p>
                            </div>

                            {/* ── Perceived Competitors section ── */}
                            {data.perceived_competitors.length > 0 && (
                                <div className="section">
                                    <h3>🤔 Your Perceived Competitors</h3>
                                    <p className="section-desc">
                                        These are the competitors you identified during onboarding.
                                        See how they actually rank in the live SERP landscape.
                                    </p>

                                    <div className="table-container">
                                        <table className="competitor-table">
                                            <thead>
                                                <tr>
                                                    <th>Domain</th>
                                                    <th>Source</th>
                                                    <th>Actual Rank</th>
                                                    <th>In Top 10?</th>
                                                    <th>Perception Gap</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.perceived_competitors.map((comp) => (
                                                    <tr
                                                        key={comp.domain}
                                                        style={{
                                                            background: comp.in_top_10
                                                                ? 'rgba(34,197,94,0.06)'
                                                                : 'rgba(239,68,68,0.04)',
                                                        }}
                                                    >
                                                        <td className="domain-cell">{comp.domain}</td>
                                                        <td>
                                                            <span style={{
                                                                fontSize: '11px',
                                                                background: comp.source === 'custom'
                                                                    ? '#f0fdf4' : '#f0f9ff',
                                                                color: comp.source === 'custom'
                                                                    ? '#166534' : '#075985',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                fontWeight: 600,
                                                            }}>
                                                                {comp.source}
                                                            </span>
                                                        </td>
                                                        <td className="rank-cell">
                                                            {comp.actual_rank !== null ? `#${comp.actual_rank}` : '—'}
                                                        </td>
                                                        <td>
                                                            <span className={`in-top10-badge ${comp.in_top_10 ? 'yes' : 'no'}`}>
                                                                {comp.in_top_10 ? '✓ Yes' : '✗ No'}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: '12px', color: '#6b7280' }}>
                                                            {comp.perception_gap}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompetitorAnalysisModal;
